<template>
  <section class="relative text-primary-dark">
    <div class="sm:flex sm:items-center pb-4">
      <div class="sm:flex-auto">
        <h1 class="text-base font-semibold text-primary-dark m-0">Payment Gateway Logs</h1>
        <p class="text-sm text-secondary">A list of all the logs in your account</p>
      </div>
    </div>

    <div class="flex items-center gap-2 pb-2">
      <BaseInput
        id="startDate"
        type="date"
        :value="search.startDate"
        label="Start Date"
        class="min-w-[200px]"
        @update:value="search.startDate = $event"
      />
      <BaseInput
        id="amount"
        type="number"
        :value="search.amount"
        :min="0"
        label="Amount"
        placeholder="Search"
        @update:value="search.amount = $event"
      />
      <BaseInput
        id="widgetName"
        type="text"
        :value="search.widgetName"
        label="Widget Name"
        placeholder="Search"
        @update:value="search.widgetName = $event"
      />
      <button
        type="button"
        class="btn-primary is-btn-size-default flex items-center gap-2"
        @click="handleSearch"
      >
        Search
      </button>
    </div>

    <LogsTable
      :logs="logs"
      @payload-modal:open="openPayloadModal"
    />

    <LogsPagination
      :pagination="pagination"
      @update:page="handlePagination"
    />

    <BaseModal
      v-if="showPayloadModal"
      title="Response Payload"
      @cancel="closePayloadModal"
    >
      <div class="mt-2 bg-gray-100 p-4 rounded-md overflow-auto max-h-96 transition-all">
        <pre class="text-sm">{{ currentPayload }}</pre>
      </div>
    </BaseModal>

    <OverlaySpinner v-if="isLoading" />

  </section>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useStore } from 'vuex';

import { PaymentGatewayLogsI, PaginationI } from '@/interfaces/interfaces';

import BaseInput from '@/components/inputs/BaseInput.vue';
import OverlaySpinner from "@/components/loader/OverlaySpinner.vue";
import BaseModal from '@/components/common/BaseModal.vue';
import LogsTable from '@/components/PaymentGatewayLogs/LogsTable.vue';
import LogsPagination from '@/components/PaymentGatewayLogs/LogsPagination.vue';

const store = useStore();

const isLoading = ref(false);
const logs = ref<PaymentGatewayLogsI[]>([]);
const pagination = ref<PaginationI>({
  lastPage: 0,
  page: 1,
  perPage: 0,
  total: 0,
});
const search = ref<{
  startDate: Date | null,
  amount: number | null,
  widgetName: string | null,
}>({
  startDate: null,
  amount: null,
  widgetName: null,
});
const showPayloadModal = ref(false);
const currentPayload = ref('');

onMounted(() => {
  getLogs();
})

const openPayloadModal = (payload: string) => {
  try {
    const parsedPayload = JSON.parse(payload);
    currentPayload.value = JSON.stringify(parsedPayload, null, 2);
    showPayloadModal.value = true;
  } catch (error) {
    console.error('Error parsing payload:', error);
    // Fallback to raw string if JSON parsing fails
    currentPayload.value = payload;
    showPayloadModal.value = true;
  }
};

const closePayloadModal = () => {
  showPayloadModal.value = false;
  currentPayload.value = '';
};

const getLogs = async (isPagination = false) => {
  isLoading.value = true;

  store
    .dispatch('getPaymentGatewayLogs',{
      page: isPagination ? pagination.value.page : null,
      startDate: search.value.startDate,
      amount: search.value.amount,
      widgetName: search.value.widgetName,
    })
    .then(() => {
      logs.value = store.state.paymentGatewayLogs?.data || [];
      pagination.value = store.state.paymentGatewayLogs?.pagination || {};
      isLoading.value = false;
    })
    .catch((error) => {
      console.error(error);
      isLoading.value = false;
    })
}

const handleSearch = () => {
  getLogs();
}

const handlePagination = (page: number) => {
  pagination.value.page = page;
  search.value.startDate = null;
  search.value.amount = null;
  search.value.widgetName = null;
  getLogs(true);
}
</script>
